import React from 'react';
import { Button, ButtonGroup } from '@tesla/design-system-react';
import { object, string } from 'prop-types';
import { Multistep, useMultistepApi, useMultistepState, SchemaFields } from 'informed';
import _findIndex from 'lodash/findIndex';
import { useIntl } from 'react-intl';
import isEmpty from 'lodash/isEmpty';
import {
  EEOSection,
  JobDetailSection,
  LegalSection,
  PersonalSection,
  PersonalLegalEEOSection,
  WorkExperience,
} from '../../Sections';
import messages from './messages';
import style from './style.module.scss';
import globalStyle from '../../../style.module.scss';
import { useTCLFormApi, useTCLFormState } from '../../../hooks';

const ApplicationStep = ({ id, title, fields, ...props }) => {
  const intl = useIntl();
  const formHelper = useTCLFormApi();
  const { formatMessage } = intl;
  const {
    nextLabel = formatMessage(messages.NextLabel),
    previousLabel = formatMessage(messages.PreviousLabel),
  } = props;
  const { submitLabel = formatMessage(messages.SubmitLabel), multiStep } = useTCLFormApi();
  const { nextStep, previousStep, current } = useMultistepState();
  const { next, previous } = useMultistepApi();
  const {
    tclFormState: { steps },
    actions: { changeActiveStep },
  } = useTCLFormState();

  const legalese = formHelper.schema.tclSchema.extras.legalese ?? null;
  const legalesePosition = formHelper.schema.tclSchema.extras.legalesePosition ?? 'after';

  const legaleseCopy = !isEmpty(legalese) ? (
    <div className="legalese">
      <p
        className="tds-text--400 tds-text--caption tds-text--contrast-low"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: legalese }}
      />
    </div>
  ) : null;

  const renderStep = () => {
    switch (id) {
      case 'personal':
        return <PersonalSection title={title} fields={fields} {...props} />;
      case 'personallegaleeo':
        return <PersonalLegalEEOSection title={title} fields={fields} {...props} />;
      case 'job':
        return <JobDetailSection title={title} fields={fields} {...props} />;
      case 'legal':
        return <LegalSection title={title} fields={fields} {...props} />;
      case 'eeo':
        return <EEOSection title={title} fields={fields} {...props} />;
      case 'workExperience':
        return <WorkExperience title={title} fields={fields} {...props} />;
      default:
        return <SchemaFields title={title} fields={fields.other} />;
    }
  };

  const currentIndex = _findIndex(multiStep, ({ id: stepId }) => stepId === current);

  return (
    <Multistep.Step step={id}>
      <h2 className={`tds-text--h3 tds-text-eyebrow ${style.EyebrowOverride}`}>
        {steps.size > 1 && (
          <span className={`${style.ApplicationStepEyebrow}`}>
            {formatMessage(messages.Progress, {
              current: currentIndex + 1,
              total: steps.size,
            })}
          </span>
        )}
        {title}
      </h2>
      <div id={`step--${id}`} className={`step--${id} tds-form-fieldset`}>
        {renderStep()}
      </div>
      {fields.submit && legalesePosition === 'before' && legaleseCopy}
      <ButtonGroup className={`${globalStyle.ButtonGroup} ${style.ApplicationStepActions}`}>
        {!!previousStep && (
          <Button
            name="previous"
            className="tds-btn--secondary"
            data-action-type="previous"
            onClick={() => {
              previous();
              changeActiveStep(currentIndex - 1);
              window.scrollTo(0, 0);
            }}
          >
            {previousLabel}
          </Button>
        )}
        {!!nextStep && (
          <Button
            name="next"
            data-action-type="next"
            onClick={() => {
              next();
              changeActiveStep(currentIndex + 1);
              window.scrollTo(0, 0);
            }}
          >
            {nextLabel}
          </Button>
        )}

        {!nextStep && (
          <Button type="submit" data-action-type="submit">
            {submitLabel}
          </Button>
        )}
      </ButtonGroup>
      {fields.submit && legalesePosition === 'after' && legaleseCopy}
    </Multistep.Step>
  );
};

ApplicationStep.propTypes = {
  id: string.isRequired,
  title: string,
  nextLabel: string,
  previousLabel: string,
  // eslint-disable-next-line react/forbid-prop-types
  fields: object,
};

ApplicationStep.defaultProps = {
  title: '',
  nextLabel: undefined,
  previousLabel: undefined,
  fields: {
    other: [],
  },
};

export default ApplicationStep;
